import React, { useState, useEffect } from 'react';
import {
  TextField,
  Autocomplete,
  CircularProgress,
  styled,
  Popper,
} from '@mui/material';
import axios from 'axios';
import api from '../api/axios';
import { AstroDetailsFormStyles } from './StyledComponents';

const CustomLocationAutocomplete = ({ onSelect, defaultLat, defaultLng }) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(null); // actual selected value

  // 👉 Fetch default location name from lat/lng
  useEffect(() => {
    const fetchDefaultLocation = async () => {
      if (defaultLat && defaultLng) {
        try {
          const res = await axios.get(`${api}/places/reverse-geocode`, {
            params: { lat: defaultLat, lng: defaultLng },
          });
          if (res.data) {
            const place = {
              label: res.data.label,
              place_id: res.data.place_id,
              lat: res.data.lat,
              lng: res.data.lng,
            };
            setValue(place);
            setInputValue(place?.label ?? '');
            setOptions([place]); // preload into options
            if (onSelect) onSelect(res.data);
          }
        } catch (err) {
          console.error('Reverse geocode failed:', err);
        }
      }
    };
    fetchDefaultLocation();
  }, [defaultLat, defaultLng]);

  const handleInputChange = async (event, newInputValue) => {
    setInputValue(newInputValue);
    if (!newInputValue) return;

    setLoading(true);
    try {
      const response = await axios.get(`${api}/places/autocomplete`, {
        params: {
          input: newInputValue,
        },
      });
      setOptions(response.data.predictions || []);
    } catch (error) {
      console.error('Autocomplete fetch error:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledAutocomplete
      freeSolo
      value={value}
      options={options}
      loading={loading}
      slots={{
        popper: StyledPopper,
      }}
      getOptionLabel={(option) => option.label || ''}
      onChange={async (event, newValue) => {
        setValue(newValue);
        if (newValue?.place_id) {
          try {
            const details = await axios.get(`${api}/places/place-details`, {
              params: { place_id: newValue.place_id },
            });
            if (onSelect) onSelect(details.data); // includes lat/lng
          } catch (err) {
            console.error('Failed to fetch place details', err);
          }
        }
      }}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Place of Birth"
          placeholder="Enter your city"
          variant="outlined"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)(AstroDetailsFormStyles);
const StyledPopper = (props) => (
  <Popper
    {...props}
    placement="bottom-start"
    modifiers={[
      {
        name: 'flip',
        enabled: false, // Prevent flipping to top
      },
      {
        name: 'preventOverflow',
        options: {
          altAxis: false, // Prevent overflow along the Y axis
        },
      },
    ]}
    sx={{
      maxHeight: 300,
      overflowY: 'auto',
      zIndex: 1300,
    }}
  />
);

export default CustomLocationAutocomplete;
